'use client'

import { forwardRef, useCallback, useImperativeHandle, useMemo, useRef, useState } from 'react'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { useTranslations } from 'next-intl'
import { z } from 'zod'
import Flag from 'react-flagkit'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from '@/components/ui/form'
import { Input } from '@/components/ui/input'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs'
import { Badge } from '@/components/ui/badge'
import { Skeleton } from '@/components/ui/skeleton'
import { PropertyFieldInput, PropertyTranslationsParams, PropertyType } from '@/types/property'
import { KeyIcon, LanguagesIcon, ListIcon, PlusCircleIcon, SaveIcon, TablePropertiesIcon, XIcon } from 'lucide-react'
import { useMetaStore } from '@/store'
import { useScrollToError, useUnsavedChangesGuard } from '@/hooks'
import { setValidationErrors } from '@/lib/utils'
import { Separator } from '@/components/ui/separator'
import { FormModeEnum } from '@/enums'
import PropertyFieldsManager, { FieldsManagerRef } from '@/components/partials/properties/fields-manager'
import { UnsavedChangesGuardDialog } from '@/components/partials'

type PropsType = {
  property?: PropertyType
  onSubmit: (data: any) => void
  onSubmitAndNew?: (data: any) => void
  onCancel: () => void
  isLoading?: boolean
  mode: FormModeEnum
}

export type PropertyFormRef = {
  handleValidationErrors: (error: any) => void
  reset: () => void
}

type FormData = {
  key: string
  translations: PropertyTranslationsParams
}

const PropertyForm = forwardRef<PropertyFormRef, PropsType>(
  ({ property, onSubmit, onSubmitAndNew, onCancel, isLoading = false, mode }, ref) => {
    const t = useTranslations()
    const { scrollToFirstError } = useScrollToError()

    const { metaState } = useMetaStore()
    const locales = useMemo(() => metaState.response?.locales || [], [metaState.response?.locales])

    // State for managing fields and removed field IDs
    const [fields, setFields] = useState<PropertyFieldInput[]>(property?.fields || [])
    const [removeFieldIds, setRemoveFieldIds] = useState<string[]>([])
    const [fieldValidationErrors, setFieldValidationErrors] = useState<Record<string, string>>({})

    // Ref for the PropertyFieldsManager component
    const fieldsManagerRef = useRef<FieldsManagerRef>(null)

    const getCountryCodeFromLocale = (localeCode: string): string => {
      const parts = localeCode.split('-')
      return parts.length > 1 ? parts[1] : parts[0].toUpperCase()
    }
    const hasLocaleError = (localeCode: string): boolean => {
      return !!formErrors.translations?.[localeCode]?.name
    }

    const schema = useMemo(() => {
      const translationsSchema: Record<string, z.ZodObject<{ name: z.ZodString }>> = {}

      locales.forEach(locale => {
        translationsSchema[locale.fullCode] = z.object({
          name: z.string().min(1, t('not_blank'))
        })
      })

      return z.object({
        key: z.string().min(1, t('not_blank')),
        translations: z.object(translationsSchema)
      })
    }, [locales, t])

    const getDefaultValues = useCallback((): FormData => {
      const defaultTranslations: PropertyTranslationsParams = {}

      locales.forEach(locale => {
        const translation = property?.translations?.[locale.fullCode]
        defaultTranslations[locale.fullCode] = {
          name: translation?.name ?? ''
        }
      })

      return {
        key: property?.key ?? '',
        translations: defaultTranslations
      }
    }, [locales, property])

    type SchemaType = z.infer<typeof schema>

    const form = useForm<SchemaType>({
      resolver: zodResolver(schema),
      defaultValues: getDefaultValues() as SchemaType,
      mode: 'onChange'
    })
    const { isNavigationBlocked, confirmNavigation, cancelNavigation } = useUnsavedChangesGuard(form.formState.isDirty)

    const formErrors = form.formState.errors

    // Use scroll to error hook

    useImperativeHandle(
      ref,
      () => ({
        handleValidationErrors: (error: any) => {
          if (error.response?.status === 422) {
            const errorItems = error.response.data.items || {}

            // Extract field validation errors
            const fieldErrors: Record<string, string> = {}
            Object.entries(errorItems).forEach(([key, message]) => {
              // Check if the error is related to a field
              if (key.startsWith('fields[')) {
                // Convert array notation to dot notation for the fieldErrors object
                const fieldPath = key.replace(/\[([^\]]+)]/g, '.$1')
                fieldErrors[fieldPath] = message as string
              }
            })

            // Set field validation errors
            setFieldValidationErrors(fieldErrors)

            // Set form validation errors
            setValidationErrors(error, form.setError)
          }
          scrollToFirstError()
        },
        reset: () => form.reset()
      }),
      [form, scrollToFirstError]
    )

    const handleSubmit = (data: SchemaType) => {
      // Validate field translations
      const fieldErrors: Record<string, string> = {}
      let hasFieldErrors = false

      fields.forEach((field, index) => {
        locales.forEach(locale => {
          const name = field.translations[locale.fullCode]?.name
          if (!name || name.trim() === '') {
            const errorKey = `fields.${index}.translations.${locale.fullCode}.name`
            fieldErrors[errorKey] = t('not_blank')
            hasFieldErrors = true
          }
        })
      })

      if (hasFieldErrors) {
        setFieldValidationErrors(fieldErrors)
        return // Don't submit if there are field errors
      }

      const submissionData: {
        key: string
        translations: PropertyTranslationsParams
        fields?: PropertyFieldInput[]
        removeFieldIds?: string[]
      } = {
        key: data.key,
        translations: {} as PropertyTranslationsParams
      }

      locales.forEach(locale => {
        submissionData.translations[locale.fullCode] = {
          name: data.translations[locale.fullCode]?.name || ''
        }
      })

      // Add fields if they exist
      if (fields.length > 0) {
        // Filter fields to include only necessary properties for fields with IDs
        submissionData.fields = fields.map(field => {
          // If a field has an ID (existing field being updated), only include required properties
          if (field.id) {
            // Create a new translations object with only the name field for each locale
            const filteredTranslations: Record<string, { name: string }> = {}

            // For each locale in the translations object, only include the name property
            Object.keys(field.translations).forEach(locale => {
              filteredTranslations[locale] = {
                name: field.translations[locale].name
              }
            })

            return {
              id: field.id,
              type: field.type,
              order: field.order,
              required: field.required,
              translations: filteredTranslations
            }
          }
          // For new fields (without ID), include all properties
          return field
        })
      }

      // Add removeFieldIds if in update mode, and there are fields to remove
      if (mode === FormModeEnum.UPDATE && removeFieldIds.length > 0) {
        submissionData.removeFieldIds = removeFieldIds
      }

      // Clear field validation errors before submitting
      setFieldValidationErrors({})

      onSubmit(submissionData)
    }

    const handleSubmitAndNew = (data: SchemaType) => {
      // Validate field translations
      const fieldErrors: Record<string, string> = {}
      let hasFieldErrors = false

      fields.forEach((field, index) => {
        locales.forEach(locale => {
          const name = field.translations[locale.fullCode]?.name
          if (!name || name.trim() === '') {
            const errorKey = `fields.${index}.translations.${locale.fullCode}.name`
            fieldErrors[errorKey] = t('not_blank')
            hasFieldErrors = true
          }
        })
      })

      if (hasFieldErrors) {
        setFieldValidationErrors(fieldErrors)
        return // Don't submit if there are field errors
      }

      const submissionData: {
        key: string
        translations: PropertyTranslationsParams
        fields?: PropertyFieldInput[]
        removeFieldIds?: string[]
      } = {
        key: data.key,
        translations: {} as PropertyTranslationsParams
      }

      locales.forEach(locale => {
        submissionData.translations[locale.fullCode] = {
          name: data.translations[locale.fullCode]?.name || ''
        }
      })

      // Add fields if they exist
      if (fields.length > 0) {
        // Filter fields to include only necessary properties for fields with IDs
        submissionData.fields = fields.map(field => {
          // If a field has an ID (existing field being updated), only include required properties
          if (field.id) {
            // Create a new translations object with only the name field for each locale
            const filteredTranslations: Record<string, { name: string }> = {}

            // For each locale in the translations object, only include the name property
            Object.keys(field.translations).forEach(locale => {
              filteredTranslations[locale] = {
                name: field.translations[locale].name
              }
            })

            return {
              id: field.id,
              type: field.type,
              order: field.order,
              required: field.required,
              translations: filteredTranslations
            }
          }
          // For new fields (without ID), include all properties
          return field
        })
      }

      // Add removeFieldIds if in update mode, and there are fields to remove
      if (mode === FormModeEnum.UPDATE && removeFieldIds.length > 0) {
        submissionData.removeFieldIds = removeFieldIds
      }

      // Clear field validation errors before submitting
      setFieldValidationErrors({})

      onSubmitAndNew?.(submissionData)
    }

    // Handle field changes
    const handleFieldsChange = (updatedFields: PropertyFieldInput[]) => {
      setFields(updatedFields)
    }

    // Handle field removal
    const handleRemoveField = (fieldId: string) => {
      setRemoveFieldIds(prev => [...prev, fieldId])
    }

    const isCreateModeWithSubmitAndNew = mode === FormModeEnum.CREATE && !!onSubmitAndNew

    if (!metaState.response?.locales) {
      return (
        <Card>
          <CardHeader>
            <Skeleton className="h-6 w-48" />
          </CardHeader>
          <CardContent className="space-y-6">
            <div className="space-y-2">
              <Skeleton className="h-4 w-12" />
              <Skeleton className="h-10 w-full" />
            </div>
            <div className="space-y-4">
              <Skeleton className="h-6 w-32" />
              <div className="flex gap-2">
                <Skeleton className="h-10 w-24" />
                <Skeleton className="h-10 w-24" />
                <Skeleton className="h-10 w-24" />
              </div>
              <div className="space-y-4">
                <Skeleton className="h-4 w-32" />
                <Skeleton className="h-20 w-full" />
              </div>
            </div>
          </CardContent>
        </Card>
      )
    }

    return (
      <Form {...form}>
        <form
          onSubmit={form.handleSubmit(
            isCreateModeWithSubmitAndNew ? handleSubmitAndNew : handleSubmit,
            scrollToFirstError
          )}
          className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <TablePropertiesIcon className="h-5 w-5" />
                {mode === FormModeEnum.CREATE ? t('create_property') : t('edit_property')}
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-6">
              {/* Key Field */}
              <div className="grid grid-cols-1 gap-4 md:grid-cols-1 lg:grid-cols-2">
                <FormField
                  control={form.control}
                  name="key"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel required className="flex items-center gap-2">
                        <KeyIcon className="h-4 w-4" />
                        {t('key')}
                      </FormLabel>
                      <FormControl>
                        <Input placeholder={t('key')} {...field} autoFocus />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>

              {/* Translations */}
              <div className="space-y-4">
                <div className="flex items-center gap-2">
                  <LanguagesIcon className="h-5 w-5" />
                  <h3 className="text-lg font-semibold">{t('translations')}</h3>
                </div>

                {locales.length > 0 ? (
                  <Tabs
                    defaultValue={locales[0].fullCode}
                    className="w-full"
                    onValueChange={value => {
                      setTimeout(() => {
                        const input = document.querySelector(`[name="translations.${value}.name"]`) as HTMLInputElement
                        if (input) {
                          input.focus()
                        }
                      }, 100)
                    }}>
                    <TabsList className="min-h-10.5 w-full">
                      {locales.map(locale => {
                        const hasError = hasLocaleError(locale.fullCode)
                        return (
                          <TabsTrigger
                            key={locale.fullCode}
                            value={locale.fullCode}
                            className={`relative flex items-center gap-2 ${
                              hasError ? 'text-destructive border-destructive' : ''
                            }`}>
                            <Flag country={getCountryCodeFromLocale(locale.fullCode)} size={16} />
                            <span className="hidden sm:inline">{locale.name}</span>
                            {hasError && (
                              <div className="bg-destructive absolute -top-1 -right-1 h-2 w-2 animate-pulse rounded-full" />
                            )}
                          </TabsTrigger>
                        )
                      })}
                    </TabsList>

                    {locales.map(locale => {
                      const hasError = hasLocaleError(locale.fullCode)
                      return (
                        <TabsContent
                          key={locale.fullCode}
                          value={locale.fullCode}
                          forceMount
                          className="mt-6 space-y-4 data-[state=inactive]:hidden">
                          <div className="flex items-center gap-2">
                            <Flag country={getCountryCodeFromLocale(locale.fullCode)} size={20} />
                            <h4 className="text-lg font-semibold">{locale.name}</h4>
                            <Badge variant="outline" className="text-xs">
                              {locale.fullCode}
                            </Badge>
                            {hasError && (
                              <Badge variant="destructive" className="animate-pulse text-xs">
                                {t('error')}
                              </Badge>
                            )}
                          </div>

                          <FormField
                            control={form.control}
                            name={`translations.${locale.fullCode}.name`}
                            render={({ field }) => (
                              <FormItem>
                                <FormLabel required>{t('name')}</FormLabel>
                                <FormControl>
                                  <Input
                                    placeholder={t('name')}
                                    className={`${hasError ? 'border-destructive focus:border-destructive' : ''}`}
                                    {...field}
                                  />
                                </FormControl>
                                <FormMessage />
                              </FormItem>
                            )}
                          />
                        </TabsContent>
                      )
                    })}
                  </Tabs>
                ) : (
                  <div className="py-8 text-center">
                    <p className="text-muted-foreground">{t('no_languages_available')}</p>
                  </div>
                )}
              </div>
              <Separator />

              {/* Fields */}
              <div className="space-y-4">
                <div className="flex items-center justify-between gap-2">
                  <div className="flex items-center gap-2">
                    <ListIcon className="h-5 w-5" />
                    <h3 className="text-lg font-semibold">{t('fields')}</h3>
                  </div>
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={() => fieldsManagerRef.current?.handleAddField()}>
                    <PlusCircleIcon className="h-4 w-4" />
                    {t('add')}
                  </Button>
                </div>
                <PropertyFieldsManager
                  ref={fieldsManagerRef}
                  fields={fields}
                  locales={locales}
                  onChange={handleFieldsChange}
                  onRemoveField={handleRemoveField}
                  validationErrors={fieldValidationErrors}
                />
              </div>

              <Separator />

              <div className="flex items-center justify-between gap-3 pt-1">
                <Button
                  type="button"
                  variant="outline"
                  onClick={onCancel}
                  disabled={isLoading || form.formState.isSubmitting}>
                  <XIcon className="h-4 w-4" />
                  <span className="hidden sm:inline">{t('cancel')}</span>
                </Button>
                <div className="flex items-center gap-2">
                  {isCreateModeWithSubmitAndNew && (
                    <Button
                      type="button"
                      onClick={() => form.handleSubmit(handleSubmit, scrollToFirstError)()}
                      disabled={isLoading || form.formState.isSubmitting}>
                      <SaveIcon className="h-4 w-4" />
                      <span className="hidden sm:inline">
                        {isLoading || form.formState.isSubmitting ? t('saving') : t('save')}
                      </span>
                    </Button>
                  )}
                  <Button type="submit" disabled={isLoading || form.formState.isSubmitting}>
                    {isCreateModeWithSubmitAndNew ? (
                      <PlusCircleIcon className="h-4 w-4" />
                    ) : (
                      <SaveIcon className="h-4 w-4" />
                    )}
                    {isLoading || form.formState.isSubmitting
                      ? t('saving')
                      : isCreateModeWithSubmitAndNew
                        ? t('save_and_add_new')
                        : t(mode)}
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>
          <UnsavedChangesGuardDialog
            open={isNavigationBlocked}
            onConfirm={confirmNavigation}
            onCancel={cancelNavigation}
          />
        </form>
      </Form>
    )
  }
)

PropertyForm.displayName = 'PropertyForm'

export default PropertyForm
